import React, { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import ReactGA from 'react-ga4';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { Warning } from '@fikasio/react-ui-components';
import TasksView from 'components/tasks/TasksView';
import { calculateOverloadInTheFuture, DEFAULT_TASKS_FILTER_DRAFT, TasksFilterDraft } from 'components/tasks/utils';
import BasePage from 'components/UI/BasePage';
import { FEATURES } from 'services/features/catalog';
import { useFeatureEnabled } from 'services/features/hooks';
import { setPaneContent } from 'services/pane/slice';
import { useGetTasksQuery } from 'services/tasks/api';
import { TaskListFilter } from 'services/tasks/types';
import { round } from 'utils/maths';
import { getUserTimeZone } from 'utils/timezone';

const startOfLocalDay = (dateTime: DateTime) => (
  dateTime.set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
);

const dueAtRange = (from: DateTime, to: DateTime): TaskListFilter => ({
  dueAt: {
    $gte: startOfLocalDay(from).toUTC().toISO()!,
    $lt: startOfLocalDay(to).toUTC().toISO()!,
  },
});

const homepageTasksFilter: TasksFilterDraft = {
  ...DEFAULT_TASKS_FILTER_DRAFT,
  completion: 'ALL',
  includeArchived: true,
};

const HomePage = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const { enabled: tasksEnabled } = useFeatureEnabled(FEATURES.tasks);
  const timeZone = getUserTimeZone();
  const todayKey = DateTime.now().setZone(timeZone).toFormat('yyyy-MM-dd');
  const { todayFilter, thisWeekFilter } = useMemo(() => {
    const now = DateTime.fromISO(todayKey, { zone: timeZone });
    return {
      todayFilter: dueAtRange(now, now.plus({ days: 1 })),
      thisWeekFilter: dueAtRange(now.plus({ days: 1 }), now.plus({ days: 7 })),
    };
  }, [todayKey, timeZone]);
  const { data: todayData } = useGetTasksQuery({
    filter: todayFilter,
  }, { skip: !tasksEnabled });
  const { data: thisWeekData } = useGetTasksQuery({
    filter: thisWeekFilter,
  }, { skip: !tasksEnabled });
  const dispatch = useDispatch();

  const tasksForToday = todayData?.tasks;
  const overloadToday = calculateOverloadInTheFuture(tasksForToday || []);

  const tasksForThisWeek = thisWeekData?.tasks;
  const overloadThisWeek = calculateOverloadInTheFuture(tasksForThisWeek || []);

  useEffect(() => {
    ReactGA.send({
      hitType: 'pageview',
      page: location.pathname,
    });
  }, []);

  return (
    <>
      <Helmet>
        <title>{t('home')}</title>
      </Helmet>
      <BasePage>
        {
          tasksEnabled && (
            <>
              <h4>{t('tasksDueForToday')}</h4>
              {
                !!overloadToday && (
                  <Warning>
                    {
                      t('youHaveTooMuchToDoToday', {
                        avgHours: round(overloadToday.averageHoursPerDayBeforeDate, 1),
                      })
                    }
                  </Warning>
                )
              }
              <TasksView
                defaultFilter={homepageTasksFilter}
                extraFilter={todayFilter}
                onTaskClick={
                  taskId => dispatch(
                    setPaneContent({
                      type: 'TASK',
                      id: taskId,
                    })
                  )
                }
                showAddButton
                showCompletionFilter
                tasks={tasksForToday}
              />
              <h4>{t('tasksDueForThisWeek')}</h4>
              {
                !!overloadThisWeek && (
                  <Warning>
                    {
                      t('youHaveTooMuchToDoNext7Days', {
                        avgHours: round(overloadThisWeek.averageHoursPerDayBeforeDate, 1),
                      })
                    }
                  </Warning>
                )
              }
              <TasksView
                defaultFilter={homepageTasksFilter}
                extraFilter={thisWeekFilter}
                onTaskClick={
                  taskId => dispatch(
                    setPaneContent({
                      type: 'TASK',
                      id: taskId,
                    })
                  )
                }
                showAddButton
                showCompletionFilter
                tasks={tasksForThisWeek}
              />
            </>
          )
        }
      </BasePage>
    </>
  );
}

export default HomePage;
